import { CircularProgress, InputAdornment, TextField, Tooltip } from "@material-ui/core";
import { useSnackbar } from "notistack";
import React, { ChangeEvent, useCallback, useEffect, useState } from "react";

import { ApiResult } from "../../rest/baseApi";
import { AjaxResult } from "../../enums/ajaxResult";

interface Props {
    entityId: number;
    notes: string | null;
    tooltipText: string | undefined;
    onNotesUpdated: () => void;
    disabled: boolean;
    updateNotesRequest: (request: {
        id: number;
        notes: string;
    }, abortController?: AbortController) => Promise<ApiResult<unknown>>
}

export function NotesEditField({
    notes,
    updateNotesRequest,
    entityId,
    tooltipText,
    onNotesUpdated,
    disabled,
}: Props) {
    const { enqueueSnackbar } = useSnackbar();
    const [editedNotes, setEditedNotes] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setEditedNotes(e.target.value);
    }, []);

    const handleSubmit = useCallback((e: React.FormEvent) => {
        if (editedNotes === notes) return;
        e.preventDefault();
        setIsLoading(true);
        updateNotesRequest({ id: entityId, notes: editedNotes })
            .then((r) => {
                if (r.result === AjaxResult.Success) {
                    setIsLoading(false);
                    enqueueSnackbar("The note has been updated", { variant: "success" });
                    onNotesUpdated();
                } else if (r.result !== AjaxResult.Cancelled) {
                    enqueueSnackbar("Unable to update note", { variant: "error" });
                    setIsLoading(false);
                }
            });
    }, [editedNotes, notes, entityId, onNotesUpdated, updateNotesRequest, enqueueSnackbar]);

    useEffect(() => {
        setEditedNotes(notes || "");
    }, [notes]);

    return (
        <form onSubmit={handleSubmit}>
            <Tooltip title={tooltipText} enterDelay={700}>
                <TextField
                    disabled={isLoading || disabled}
                    value={editedNotes}
                    onChange={handleChange}
                    onBlur={handleSubmit}
                    style={{ minWidth: "150px" }}
                    InputProps={{
                        endAdornment: isLoading
                            ? (
                                <InputAdornment position="end">
                                    <CircularProgress size={24} color="inherit" />
                                </InputAdornment>
                            )
                            : <div style={{ width: 28 }}></div>
                    }}
                />
            </Tooltip>
        </form>
    );
}